import { EstablishmentLeadsFunnelUpdateValidationSchema, MaintenanceRequiredDocumentValidationSchema } from "../../schemas/update.schema";
import Button from "@/common/components/Button";
import { XIcon } from "lucide-react";
import { FileUploader } from "@/common/components/FileUploader";
import ResumeBox from "@/common/components/ResumeBox";
import { DateInput } from "@/common/components/DateInput/DateInput";
import { useFormContext } from "react-hook-form";
import useBlobs from "@/common/hooks/queries/useBlobs";
import { ErrorMessage } from "@/common/components/ErrorMessage";

interface MaintenanceRequiredDocumentItemProps {
  index: number;
  maintenanceRequiredDocument: MaintenanceRequiredDocumentValidationSchema;
  handleRemove: VoidFunction;
  disabled?: boolean;
  allowUpdate?: boolean;
}

const MaintenanceRequiredDocumentItem = ({
  index,
  maintenanceRequiredDocument,
  handleRemove,
  disabled,
  allowUpdate,
}: MaintenanceRequiredDocumentItemProps) => {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<EstablishmentLeadsFunnelUpdateValidationSchema>();

  const validUntil = maintenanceRequiredDocument.valid_until;

  const issueDate = maintenanceRequiredDocument.issue_date;

  const fileAttributes = maintenanceRequiredDocument.file_attachment_attributes;

  const {
    mutateAsync: uploadBlob,
    isPending: uploadBlobIsPending,
  } = useBlobs();

  const handleUploadDocument = async (file: File) => {
    const blob = await uploadBlob({ file, filename: file.name });

    const blobId = blob?.data?.data?.id;

    setValue(`maintenance_required_documents_attributes.${index}.file_attachment_attributes.blob_id`, blobId);

    setValue(`maintenance_required_documents_attributes.${index}.file_attachment_attributes.file_url`, URL.createObjectURL(file));

    setValue(`maintenance_required_documents_attributes.${index}.file_attachment_attributes.file_name`, file.name);
  }

  return (
    <div className="flex items-start lg:items-center gap-3 bg-white-100 p-3 rounded-lg lg:bg-transparent lg:p-0 lg:rounded-none">
      {allowUpdate && (
        <Button
          variant="link-white"
          onClick={handleRemove}
          disabled={disabled}
        >
          <XIcon strokeWidth={1} size={24} className="text-danger" />
        </Button>
      )}

      <div className="flex flex-col lg:flex-row flex-1 gap-3">
        <div className="flex flex-col gap-1">
          <FileUploader
            title="Anexe documentos"
            className="min-h-[108px] w-[192px]"
            accept=".png, .jpg, .webp, .pdf"
            handleChange={handleUploadDocument}
            disabled={uploadBlobIsPending || disabled}
            currentFileUrl={fileAttributes?.file_url}
            fileName={fileAttributes?.file_name}
          />

          {errors.maintenance_required_documents_attributes?.[index]?.file_attachment_attributes?.file_name?.message && (
            <ErrorMessage
              name={`maintenance_required_documents_attributes.${index}.file_attachment_attributes.file_name`}
              message={errors.maintenance_required_documents_attributes?.[index]?.file_attachment_attributes?.file_name.message}
            />
          )}
        </div>

        <div className="flex flex-col gap-2 flex-1">
          <ResumeBox
            label="Nome do documento"
            value={maintenanceRequiredDocument.document.display_name}
            padding="small"
            boxClassName="p-2 rounded-md"
            labelClassName="text-xs text-[11px]"
            valueClassName="text-xs font-bold"
          />

          <div className="flex flex-col xl:flex-row gap-3">
            <DateInput
              label="Data de emissão"
              control={control}
              fieldName={`maintenance_required_documents_attributes.${index}.issue_date`}
              errorMessage={errors.maintenance_required_documents_attributes?.[index]?.issue_date?.message || ''}
              disabled={disabled}
              value={issueDate}
              className="flex-1"
            />

            <DateInput
              label="Validade do documento"
              control={control}
              fieldName={`maintenance_required_documents_attributes.${index}.valid_until`}
              errorMessage={errors.maintenance_required_documents_attributes?.[index]?.valid_until?.message || ''}
              disabled={disabled}
              value={validUntil}
              className="flex-1"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default MaintenanceRequiredDocumentItem;
